import React, { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle, XCircle, Download, Loader2, Clock } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DataTable } from '../../components/ui/DataTable';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Pagination } from '../../components/ui/Pagination';
import { showInfo } from '../../components/ui/Toast';
import { mockBatches } from '../../data/mockData';
import { PaymentBatch } from '../../types';
interface PostApprovalViewProps {
  batchId: string;
  onBack: () => void;
}
export function PostApprovalView({
  batchId,
  onBack
}: PostApprovalViewProps) {
  const [batch, setBatch] = useState<PaymentBatch | undefined>(mockBatches.find(b => b.id === batchId));
  const [progress, setProgress] = useState(0);
  const [filter, setFilter] = useState<'all' | 'success' | 'failed'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // Simulate processing progress if status is PaymentInitiated
  useEffect(() => {
    if (batch?.status === 'PaymentInitiated' && progress < 100) {
      const timer = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(timer);
            return 100;
          }
          return prev + 5;
        });
      }, 200);
      return () => clearInterval(timer);
    }
  }, [batch?.status, progress]);
  if (!batch) return <div data-id="element-570">Batch not found</div>;
  const handleDownload = () => {
    showInfo('Download Started', 'The results file is being generated.');
  };
  // Compute counts for tabs
  const successRecords = batch.records.filter(r => r.status === 'Completed' || r.status === 'PaymentInitiated');
  const failedRecords = batch.records.filter(r => r.status === 'Failed' || r.status === 'Rejected');
  const filteredRecords = batch.records.filter(r => {
    if (filter === 'success') return r.status === 'Completed' || r.status === 'PaymentInitiated';
    if (filter === 'failed') return r.status === 'Failed' || r.status === 'Rejected';
    return true;
  });
  // Pagination logic
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const paginatedRecords = filteredRecords.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const tabItems = [{
    key: 'all' as const,
    label: 'All Records',
    count: batch.records.length
  }, {
    key: 'success' as const,
    label: 'Successful',
    count: successRecords.length
  }, {
    key: 'failed' as const,
    label: 'Failed',
    count: failedRecords.length
  }];
  return <div className="space-y-6" data-id="element-571">
      <div className="flex items-center justify-between" data-id="element-572">
        <div className="flex items-center space-x-4" data-id="element-573">
          <Button variant="ghost" size="sm" onClick={onBack} data-id="element-574">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-575" />
            Back to List
          </Button>
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-576">
            Batch: {batch.fileName}
          </h2>
          <StatusBadge status={batch.status} data-id="element-577" />
        </div>

        <Button variant="outline" onClick={handleDownload} leftIcon={<Download className="w-4 h-4" data-id="element-579" />} data-id="element-578">
          Download Results
        </Button>
      </div>

      {/* Progress Section */}
      {batch.status === 'PaymentInitiated' && <Card className="p-6 bg-blue-50 border-blue-100" data-id="element-580">
          <div className="flex items-center justify-between mb-2" data-id="element-581">
            <h3 className="font-medium text-blue-900 flex items-center" data-id="element-582">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" data-id="element-583" />
              Processing Payments...
            </h3>
            <span className="text-sm font-bold text-blue-700" data-id="element-584">{progress}%</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2.5" data-id="element-585">
            <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{
          width: `${progress}%`
        }} data-id="element-586"></div>
          </div>
          <p className="text-xs text-blue-600 mt-2" data-id="element-587">
            Communicating with RAAST and Core Banking System. Please do not
            close this window.
          </p>
        </Card>}

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-588">
        <Card className="p-4 border-l-4 border-l-blue-500" data-id="element-589">
          <p className="text-sm text-gray-500" data-id="element-590">Total Records</p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-591">
            {batch.totalRecords}
          </p>
        </Card>
        <Card className="p-4 border-l-4 border-l-green-500" data-id="element-592">
          <p className="text-sm text-gray-500" data-id="element-593">Successful</p>
          <p className="text-2xl font-bold text-green-600" data-id="element-594">
            {successRecords.length}
          </p>
        </Card>
        <Card className="p-4 border-l-4 border-l-red-500" data-id="element-595">
          <p className="text-sm text-gray-500" data-id="element-596">Failed</p>
          <p className="text-2xl font-bold text-red-600" data-id="element-597">
            {failedRecords.length}
          </p>
        </Card>
        <Card className="p-4 border-l-4 border-l-[#1B6B3A]" data-id="element-598">
          <p className="text-sm text-gray-500" data-id="element-599">Total Amount</p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-600">
            PKR {batch.totalAmount.toLocaleString()}
          </p>
        </Card>
      </div>

      {/* Results Table */}
      <Card title="Transaction Details" data-id="element-601">
        <div className="border-b border-gray-200 mb-4" data-id="element-602">
          <nav className="-mb-px flex space-x-6 px-6" data-id="element-603">
            {tabItems.map(tab => <button key={tab.key} onClick={() => {
            setFilter(tab.key);
            setCurrentPage(1);
          }} className={`
                  whitespace-nowrap py-3.5 px-1 border-b-2 font-medium text-sm flex items-center gap-2
                  ${filter === tab.key ? 'border-[#1B6B3A] text-[#1B6B3A]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `} data-id="element-604">
                {tab.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${filter === tab.key ? 'bg-[#1B6B3A]/10 text-[#1B6B3A]' : 'bg-gray-100 text-gray-500'}`} data-id="element-605">
                  {tab.count}
                </span>
              </button>)}
          </nav>
        </div>

        <div className="overflow-x-auto" data-id="element-606">
          <DataTable columns={[{
          header: 'Receiver IBAN',
          accessor: r => <span className="font-mono text-xs" data-id="element-608">{r.receiverIban}</span>
        }, {
          header: 'Beneficiary Name',
          accessor: 'receiverName'
        }, {
          header: 'Account Title',
          accessor: r => r.titleFetchResult ? <span className="font-semibold text-green-700 text-xs" data-id="element-609">
                      {r.titleFetchResult}
                    </span> : <span className="text-gray-400 text-xs italic" data-id="element-610">—</span>
        }, {
          header: 'Amount (PKR)',
          accessor: r => <span className="font-mono" data-id="element-611">{r.amount.toLocaleString()}</span>
        }, {
          header: 'Purpose',
          accessor: r => <span className="font-mono text-xs" data-id="element-612">{r.paymentPurpose}</span>
        }, {
          header: 'Purpose Description',
          accessor: r => <div className="max-w-[160px] truncate" title={r.purposeDescription} data-id="element-613">
                    {r.purposeDescription}
                  </div>
        }, {
          header: 'Narration',
          accessor: r => <div className="max-w-[140px] truncate" title={r.narration} data-id="element-614">
                    {r.narration}
                  </div>
        }, {
          header: 'Payment Ref',
          accessor: r => <span className="text-xs text-gray-600" data-id="element-615">
                    {r.paymentReference || '—'}
                  </span>
        }, {
          header: 'Status',
          accessor: r => <StatusBadge status={r.status} size="sm" data-id="element-616" />
        }]} data={paginatedRecords} emptyMessage={`No ${filter === 'all' ? '' : filter} records found`} data-id="element-607" />
        </div>
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-617" />
      </Card>
    </div>;
}